import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { fileToDataUrl } from "@/lib/image";

export const SupportChat = ({ isAdmin }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiGet("/support/messages");
      setItems(data.items);
    } catch { /* biarkan kosong */ }
  }, []);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await apiGet("/support/unread");
      setUnread(data.unread);
    } catch { /* abaikan */ }
  }, []);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-support", handler);
    return () => window.removeEventListener("open-support", handler);
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 15000);
    return () => clearInterval(t);
  }, [loadUnread]);

  useEffect(() => {
    if (!open) return;
    setUnread(0);
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [open, load]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [items, open]);

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const dataUrl = await fileToDataUrl(f);
      if (dataUrl.length > 880_000) {
        toast.error("Gambar terlalu besar, pilih yang lebih kecil");
        return;
      }
      setImage(dataUrl);
    } catch {
      toast.error("File bukan gambar yang valid");
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !image) return;
    setSending(true);
    try {
      await apiPost("/support/messages", { message: text.trim(), image });
      setText("");
      setImage(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim pesan");
    } finally {
      setSending(false);
    }
  };

  if (isAdmin) return null;

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="support-chat-button"
        className="fixed bottom-4 right-4 z-50 flex h-11 items-center gap-2 border border-buy/50 bg-[#0d1219] px-4 font-mono text-[11px] font-bold uppercase tracking-widest text-buy shadow-2xl transition-colors hover:bg-buy hover:text-ink">
        <MessageCircle size={15} /> Support
        {unread > 0 && (
          <span data-testid="support-unread-badge"
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-sell px-1 font-mono text-[9px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-16 right-4 z-50 flex h-[26rem] w-[calc(100vw-2rem)] max-w-sm flex-col border border-white/15 bg-[#0d1219] shadow-2xl" data-testid="support-chat-panel">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div>
              <p className="font-mono text-xs font-bold uppercase tracking-widest text-white">Chat Admin</p>
              <p className="font-mono text-[9px] text-mute-dim">balasan admin masuk di sini</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-chat-close"
              className="text-mute-dim transition-colors hover:text-white">
              <X size={15} />
            </button>
          </div>
          <div ref={bodyRef} className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="support-chat-messages">
            {items === null ? (
              <p className="py-8 text-center font-mono text-xs text-mute-dim">memuat...</p>
            ) : items.length === 0 ? (
              <p className="py-8 text-center font-mono text-xs leading-relaxed text-mute-dim">
                belum ada pesan.<br />tulis pertanyaan Anda, admin akan membalas.
              </p>
            ) : (
              items.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] border px-3 py-2 font-mono text-xs leading-relaxed ${
                    m.sender === "user" ? "border-buy/30 bg-buy/10 text-white" : "border-brand/40 bg-brand/10 text-white"
                  }`}>
                    {m.sender === "admin" && <p className="mb-0.5 text-[8px] font-bold uppercase tracking-widest text-[#8b97f5]">Admin</p>}
                    {m.image && (
                      <img src={m.image} alt="lampiran" data-testid={`support-image-${m.id?.slice(0, 8)}`}
                        className="mb-1 max-w-full border border-white/10" />
                    )}
                    {m.message && m.message !== "[gambar]" && <span>{m.message}</span>}
                    <p className="mt-1 text-right text-[8px] text-mute-dim">{m.created_at?.slice(5, 16).replace("T", " ")}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          {image && (
            <div className="flex items-center gap-2 border-t border-white/10 px-3 py-2" data-testid="support-image-preview">
              <img src={image} alt="pratinjau" className="h-12 border border-white/15" />
              <button onClick={() => setImage(null)} data-testid="support-image-remove"
                className="text-mute-dim transition-colors hover:text-sell">
                <X size={14} />
              </button>
            </div>
          )}
          <form onSubmit={send} className="flex gap-2 border-t border-white/10 p-3">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="support-image-input" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="support-attach-button" title="Kirim gambar"
              className="flex h-9 w-10 shrink-0 items-center justify-center border border-white/15 text-mute transition-colors hover:border-buy/60 hover:text-buy">
              <ImagePlus size={14} />
            </button>
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
              placeholder="tulis pesan..." data-testid="support-chat-input"
              className="h-9 min-w-0 flex-1 border border-white/15 bg-ink px-3 font-mono text-xs text-white outline-none focus:border-buy/60" />
            <button type="submit" disabled={sending || (!text.trim() && !image)} data-testid="support-chat-send"
              className="flex h-9 w-10 shrink-0 items-center justify-center border border-buy bg-buy/10 text-buy transition-colors hover:bg-buy hover:text-ink disabled:opacity-50">
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
